import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gift, Copy, Check, MessageCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-ink/10 bg-cream/60 px-4 py-3">
      <p className="text-[11px] font-bold uppercase tracking-wide text-ink/50">{label}</p>
      <p className="mt-1 font-display text-2xl font-black tracking-tight text-ink" data-testid={testId}>
        {value}
      </p>
    </div>
  );
}

/**
 * Referral panel for the Billing page.
 *   <ReferralCard isPaid={isPaid} ready={!loading} />
 * Free / lapsed merchants see a locked state; paid merchants see their link + stats.
 */
export default function ReferralCard({ isPaid, ready = true }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!ready || !isPaid) return;
    api
      .get("/referral")
      .then(({ data }) => setData(data))
      .catch(() => setFailed(true));
  }, [ready, isPaid]);

  if (!ready) return <p className="text-sm text-ink/50">Loading…</p>;

  if (!isPaid) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-ink/10 bg-cream/60 px-5 py-4">
        <div className="flex items-center gap-3 text-sm text-ink/70">
          <Lock size={16} className="flex-none text-ink/40" />
          Referral rewards are for merchants on a paid plan.
        </div>
        <button
          onClick={() => navigate("/plans")}
          className="rounded-full border border-ink/15 bg-white px-4 py-2 text-sm font-semibold hover:border-hotpink hover:text-hotpink"
          data-testid="referral-upgrade"
        >
          See plans
        </button>
      </div>
    );
  }

  if (failed) return <p className="text-sm text-ink/50">Couldn't load your referral details. Please refresh.</p>;
  if (!data) return <p className="text-sm text-ink/50">Loading…</p>;

  const link = `${window.location.origin}/?ref=${data.code}`;
  const perReferral = data.reward_days_per_referral || 10;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy. Select the link and copy it manually.");
    }
  };

  const shareText = encodeURIComponent(
    `Sell on Instagram DMs with Checkout DM: automated orders, payments and shipping inside the chat. Join here: ${link}`
  );

  return (
    <div data-testid="referral-card">
      {!data.eligible && (
        <div className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Your plan isn't active right now. Days are only added while your paid plan is active, so renew to keep earning.
        </div>
      )}

      <p className="text-sm font-semibold text-ink">Your invite link</p>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <input
          readOnly
          value={link}
          onFocus={(e) => e.target.select()}
          className="min-w-0 flex-1 rounded-full border border-ink/10 bg-cream/60 px-4 py-2.5 text-sm text-ink"
          data-testid="referral-link"
        />
        <div className="flex gap-2">
          <button
            onClick={copy}
            className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2.5 text-sm font-semibold hover:border-hotpink hover:text-hotpink"
            data-testid="referral-copy"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
          </button>
          <a
            href={`https://wa.me/?text=${shareText}`}
            target="_blank"
            rel="noreferrer"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-4 py-2.5 text-sm font-semibold text-white"
            data-testid="referral-whatsapp"
          >
            <MessageCircle size={14} /> Share
          </a>
        </div>
      </div>
      <p className="mt-2 text-xs text-ink/50">
        Or give them your code: <span className="font-mono font-semibold text-ink">{data.code}</span>
      </p>

      <div className="mt-6 grid grid-cols-3 gap-3">
        <Stat label="Successful" value={data.qualified_count} testId="referral-stat-qualified" />
        <Stat label="Days earned" value={data.days_earned} testId="referral-stat-days" />
        <Stat label="Signed up" value={data.pending_count} testId="referral-stat-pending" />
      </div>

      <div className="mt-5 flex items-start gap-3 rounded-2xl border border-hotpink/20 bg-hotpink/5 px-5 py-4">
        <Gift size={16} className="mt-0.5 flex-none text-hotpink" />
        <p className="text-sm text-ink/70">
          <span className="font-semibold text-ink">Every referral pays out right away</span> — {perReferral} days land
          on your plan the moment they subscribe, no minimum needed. Refer 3 and it adds up to a full month.
        </p>
      </div>

      <ul className="mt-5 space-y-1.5 text-xs text-ink/60">
        <li>• A referral counts when the merchant you invite pays for a plan with us.</li>
        <li>• Each one adds {perReferral} days to your current plan, monthly or yearly. 3 referrals = 30 days.</li>
        <li>• Days are added automatically and you'll get a notification each time.</li>
        <li>• Only new merchants count, and your own plan must be active when they pay.</li>
        <li>• Your link is remembered for 90 days in the visitor's browser. They should sign up on the same browser and device.</li>
      </ul>

      <Link to="/referral-guide" className="mt-4 inline-block text-xs font-semibold text-hotpink hover:underline">
        How referral rewards work →
      </Link>

      {data.skipped_count > 0 && (
        <p className="mt-3 text-xs text-ink/50">
          {data.skipped_count} referral{data.skipped_count > 1 ? "s" : ""} didn't earn days because your plan had
          expired when they subscribed.
        </p>
      )}
    </div>
  );
}
